'use client';

/**
 * Docs sidebar — persistent left navigation on /docs and /docs/[slug]
 * (dash0-docs style). Sections render in the story order defined by
 * `_lib/docs-nav.ts`, always open (no per-group collapse — simpler and
 * the whole tree is ~35 links). Current page highlighted via pathname.
 *
 * Desktop (lg+): sticky, self-scrolling column. Mobile: hidden behind a
 * "Browse docs" disclosure button above the content.
 *
 * Also owns the search affordance: a search-field-shaped button that
 * opens the DocsSearchDialog overlay, plus a Cmd/Ctrl-K binding active
 * while any docs page is mounted (same convention as the dashboard
 * project switcher).
 */
import { faChevronDown, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/ui/components/ui/button';
import { Kbd } from '@repo/ui/components/ui/kbd';
import { cn } from '@repo/ui/lib/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import type { DocsNavGroup } from '../_lib/docs-nav';
import { DocsSearchDialog } from './docs-search';

export function DocsSidebar({ nav }: { nav: DocsNavGroup[] }) {
  const pathname = usePathname();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Cmd-K / Ctrl-K opens the search overlay while a docs page is mounted.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Close the mobile disclosure after navigating.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname change IS the trigger
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const navTree = (
    <nav aria-label="Docs sections" className="flex flex-col gap-6">
      {nav.map((group) => (
        <div key={group.id} className="flex flex-col gap-1.5">
          <p className="overline-sm px-3 text-text-caption-1">{group.label}</p>
          <ul className="flex flex-col">
            {group.items.map((item) => {
              const active = pathname === `/docs/${item.slug}`;
              return (
                <li key={item.slug}>
                  <Link
                    href={`/docs/${item.slug}`}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'block rounded-xxs px-3 py-1.5 font-label text-label-sm transition-colors',
                      active
                        ? 'bg-surface-hover-1 font-medium text-text-heading-1'
                        : 'text-text-caption-1 hover:bg-surface-hover-1 hover:text-text-heading-1',
                    )}
                  >
                    {item.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  const searchButton = (
    <button // ui-components: raw-button — the search box's trigger: it looks like the field it opens
      type="button"
      aria-haspopup="dialog"
      onClick={() => setSearchOpen(true)}
      className="flex w-full items-center gap-2 rounded-xxs border border-input bg-surface-field px-3 py-2 text-left transition-colors hover:border-border-action"
    >
      <FontAwesomeIcon icon={faMagnifyingGlass} className="h-3.5 w-3.5 shrink-0 text-icon-primary" />
      <span className="min-w-0 flex-1 truncate font-label text-label-sm text-text-caption-1">Search docs…</span>
      <Kbd>⌘K</Kbd>
    </button>
  );

  return (
    <div className="lg:w-64 lg:shrink-0">
      {/* Mobile — search + disclosure above the content. */}
      <div className="mb-6 flex flex-col gap-3 lg:hidden">
        {searchButton}
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          className="w-full justify-between"
        >
          Browse docs
          <FontAwesomeIcon
            icon={faChevronDown}
            className={cn('h-3.5 w-3.5 text-icon-primary transition-transform', mobileOpen && 'rotate-180')}
          />
        </Button>
        {mobileOpen && (
          <div className="rounded-xxs border border-border-primary bg-surface-card-primary p-3">{navTree}</div>
        )}
      </div>

      {/* Desktop — sticky, self-scrolling column. */}
      <aside className="sticky top-24 hidden max-h-[calc(100vh-8rem)] flex-col gap-6 overflow-y-auto pb-8 pr-2 lg:flex">
        {searchButton}
        {navTree}
      </aside>

      <DocsSearchDialog nav={nav} open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
